import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  listProjects,
  renameProject,
  deleteProject,
  copyProject,
  exportProject,
  setProjectPinned,
  markProjectSeen,
} from '../api/projects';
import { listSharedProjects, leaveSharedProject } from '../api/sharing';
import './Dashboard.css';
import Button from '../components/Button';
import FileRepository from '../components/FileRepository';
import BugReportButton from '../components/BugReportButton';
import ProcessMonitorButton from '../components/ProcessMonitorButton';
import ProfileMenuButton from '../components/ProfileMenuButton';
import ShareProjectModal from '../components/ShareProjectModal';
import AddQualityCheckModal from '../components/AddQualityCheckModal';
import SearchOverlay from '../components/SearchOverlay';
import { FolderIcon, BookIcon, DatabaseIcon, CheckCircleIcon } from '../components/icons';
import DashboardSidebar from './DashboardSidebar';
import DashboardComposer, { deriveProjectName } from './DashboardComposer';
import DashboardThread from './DashboardThread';
import { startConversation, buildProblemBrief } from './startConversation';
import { useStartProject } from './startProject';
import { DEFAULT_UNIT_SYSTEM } from '../constants/unitSystems';
import { readBoundConversation, writeBoundConversation } from './boundConversation';
import { DEFAULT_SORT } from './dashboardGroups';
import { openInNewTab } from '../utils/navigation';
import { getChatHistory } from '../api/chatHistory';
import { useAgentRun, DEFAULT_GROUP } from '../agent/useAgentRun';
import { fromSaved, localMessage } from '../panels/chatMessages';
import { MODE_AUTO, MODE_PLAN, normalizeMode, planExitMode } from '../panels/chatModes';
import versionInfo from '../version.json';
import { pollingInterval } from '../utils/polling';
import { useDocumentTitle } from '../utils/documentTitle';

// The centre pane here IS the new-project composer (and `/projects`' "New
// project" button routes to it), so the tab names that rather than the route.
const TAB_TITLE = 'New project';

export default function Dashboard({ onLogout, org, hasValidLicense = false }) {
  useDocumentTitle(TAB_TITLE);
  const [projects, setProjects] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [leaveTarget, setLeaveTarget] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [repoOpen, setRepoOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [qualityCheckTarget, setQualityCheckTarget] = useState(null);

  useEffect(() => {
    fetchProjects();
    listSharedProjects()
      .then((d) => setShared(d || []))
      .catch(() => {});
  }, []);
  // Cmd/Ctrl+K opens the project palette. The first global shortcut in the app,
  // so it lives here (Dashboard owns the state) rather than in a registry there
  // is no other user for. Bound on `window` like ProcessMonitorButton's Escape.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // HS-155: while any project has an active autonomous run, poll the project
  // list so the dashboard pill advances live. Visibility-gated (pauses on a
  // hidden tab); stops once no run is running.
  const anyRunActive = projects.some((p) => p.autoRun?.status === 'running');
  useEffect(() => {
    if (!anyRunActive) return undefined;
    const id = pollingInterval(fetchProjects, 5000);
    return () => clearInterval(id);
  }, [anyRunActive]);

  async function fetchProjects() {
    try {
      const data = await listProjects();
      setProjects(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── The conversation the centre pane is bound to ──
  //
  // The composer no longer navigates: it creates the project, starts a SERVER
  // agent run and binds this pane to it, so the back-and-forth happens here while
  // the run keeps going with the tab closed. `/learn` still navigates, via the
  // untouched `startProject.js`.
  //
  // Persisted PER TAB (sessionStorage) so a dashboard reload resumes the thread you
  // were watching while a brand-new tab starts on the composer — see
  // `boundConversation.js` for why the store is the whole point. Storage rather than
  // a URL param deliberately: `Dashboard.test.js`'s `react-router-dom` mock exposes
  // only `useNavigate`. The key itself lives in `boundConversation.js`, because `/projects`'
  // "New project" button must RELEASE this binding before navigating here — without
  // that it landed on whichever conversation was already open.
  const [threadId, setThreadId] = useState(readBoundConversation);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadSending, setThreadSending] = useState(false);
  // `{pid, run}` for a run started BEFORE the thread was bound to it — the composer's
  // create path, which is the only one where the project does not exist yet. Carried
  // as a pair because `bindThread` has not taken effect when it is set, so the run has
  // to say which project it belongs to. Handed to `useAgentRun` below.
  const [startedRun, setStartedRun] = useState(null);

  const bindThread = useCallback((projectId) => {
    setThreadMessages([]);
    // Dropped unless it belongs to the project being bound — which is the create
    // path, where it is set moments before this call. Kept forever it would re-adopt
    // a long-stale snapshot the next time the user came back to that conversation.
    setStartedRun((prev) => (prev && prev.pid === projectId ? prev : null));
    setThreadId(projectId);
    writeBoundConversation(projectId);
  }, []);

  // ── The mailbox: the conversation you are reading is READ ────────────────
  //
  // "Needs attention" is a per-viewer watermark — the owner row's
  // `lastActivityAt` against the caller's own `project_seen_` row — and until now
  // its ONLY writer was `ProjectPage.load()`, i.e. opening the canvas. But this
  // pane is where a run is watched, so a user could read the whole conversation
  // here and keep the unseen dot until they opened a canvas they had no reason
  // to visit. `ProjectPage`'s call stays; this is a second reader, not a move.
  //
  // Keyed on the ROW's `unseen` rather than firing once on bind, which buys two
  // things: a project that gains activity WHILE its thread is open stays read
  // (that is what a mailbox means — the open message does not go bold), and a
  // reload that resumes a bound thread marks it too, since `threadId` comes back
  // from localStorage without anyone clicking. That guard is also what stops the
  // loop: once cleared the effect returns early on its own re-run.
  //
  // A SHARED project never reaches here — the rail binds a conversation only for
  // owned rows, because `/api/agent-run` is owner-only.
  useEffect(() => {
    if (!threadId) return;
    const row = projects.find((p) => p.projectId === threadId);
    if (!row?.unseen) return;
    // Fire-and-forget, as `ProjectPage` does: clearing a badge must never block
    // the pane or surface an error. A failed write simply leaves the row unseen,
    // and this effect retries it on the next list refresh.
    markProjectSeen(threadId).catch(() => {});
    // Optimistic, so the dot and the activity label clear on the click rather
    // than on the next 5 s poll. `lastActivity` has to go too: `activityLabel`
    // renders from it independently of `unseen`, so clearing only the boolean
    // would leave "❓ Needs your input" sitting on a row you are looking at.
    setProjects((prev) =>
      prev.map((p) => (p.projectId === threadId ? { ...p, unseen: false, lastActivity: null } : p))
    );
  }, [threadId, projects]);

  // Strict READER of chat history. The runner appends its narration with DynamoDB
  // `list_append`, so a whole-list PUT from here would clobber it — the same
  // hazard `serverRunOwnsHistory` guards in ChatPanel, except that this surface
  // has no reason to write at all, so it simply never imports `saveChatHistory`.
  const reloadThread = useCallback(
    async (projectId) => {
      if (!projectId) return;
      try {
        const data = await getChatHistory(projectId);
        if (data?.messages?.length) {
          const next = fromSaved(data.messages);
          // Only commit a CHANGE. This is polled every 2 s while a run is live, and
          // replacing the array each time would re-render the whole transcript on a
          // timer for nothing.
          setThreadMessages((prev) =>
            prev.length === next.length &&
            prev[prev.length - 1]?.content === next[next.length - 1]?.content
              ? prev
              : next
          );
        }
      } catch (err) {
        // A 404 here is definitive rather than transient: `get_chat_history`
        // raises it only when the project ROW is absent, so the project is gone.
        // Acting on it is what covers a delete from another tab or device, which
        // the projects-list check below cannot see until the next list fetch.
        if (err?.status === 404) bindThread(null);
        /* anything else is transient — the next rev bump reloads */
      }
    },
    [bindThread]
  );

  const {
    runFor,
    start: startThreadRun,
    cancel: cancelThreadRun,
    pause: pauseThreadRun,
    resume: resumeThreadRun,
    setMode: setThreadMode,
    approve: approveThreadRun,
    answer: answerThreadParked,
    answerQuestions: answerThreadQuestions,
    savePlan: saveThreadPlan,
  } = useAgentRun(threadId, {
    onRunUpdate: () => reloadThread(threadId),
    onRunTerminal: () => reloadThread(threadId),
    // The run the composer just started, for the project it just created. Handed
    // over rather than left to be discovered, because `listAgentRuns` is an
    // eventually-consistent read of a row written milliseconds ago — see
    // `useAgentRun`. Gated on the project matching, since `bindThread` and this
    // state settle in the same render and a stale pairing would show one project's
    // run under another.
    initialRun: startedRun?.pid === threadId ? startedRun.run : null,
  });
  const threadRun = runFor(DEFAULT_GROUP);

  // ── The agent's permission mode ────────────────────────────────────────
  //
  // A PROJECT setting, so it is held here rather than in the composer: changing it
  // is a REST call, and the composer is presentational. Mirrors
  // `ProjectPage`'s own block — that page still holds the mode for the editor's
  // chats, which ENFORCE it; this is now the only place it is SET.
  //
  // With nothing bound the choice is local and there is no row to patch, so it
  // rides on the creation payload instead (see `startConversation`).
  //
  // A fresh composer opens in PLAN, not auto: the first thing a new project should
  // produce is a plan to read and approve, not a pipeline already half built. This
  // is the composer's STARTING SELECTION only — `chatModes.DEFAULT_AGENT_MODE` is
  // still `auto`, so a project that predates the field, or any row with no
  // `agentMode`, reads as auto exactly as before (see the seed below).
  const [agentMode, setAgentModeState] = useState(MODE_PLAN);

  // Seed from the bound project's row. `projects` is the same list the rail draws,
  // so this costs no extra fetch.
  useEffect(() => {
    if (!threadId) {
      // A fresh composer starts from the composer's own default rather than
      // inheriting whatever the last conversation was set to — that mode was a
      // decision about THAT piece of work, and silently carrying `manual` onto a
      // new project would read as the agent having stopped working (and carrying
      // `auto` would skip the plan the next project is supposed to open with).
      setAgentModeState(MODE_PLAN);
      return;
    }
    const row = projects.find((p) => p.projectId === threadId);
    if (!row) return;
    // The LIVE RUN OUTRANKS THE ROW, for the same reason the adoption effect below
    // exists — but that effect cannot cover this on its own, because it only fires
    // when the run's mode CHANGES. This one re-runs on every `projects` refresh (a
    // 5 s poll), so without the guard the row would clobber the mode the run is
    // actually enforcing, permanently, the moment the run resolved before the list
    // did. That ordering is the common one on a resumed thread: `listAgentRuns` is a
    // single read while `listProjects` is awaited behind the shared/owned merge.
    setAgentModeState(normalizeMode(threadRun?.agentMode || row.agentMode));
  }, [threadId, projects, threadRun?.agentMode]);

  // Adopt from the LIVE run when the two disagree. The run reports what is actually
  // being enforced, and a mode change takes a moment to land — so between the click
  // and the write the row and the run can legitimately differ, and the selector must
  // show what is in force rather than what was asked for. Still needed alongside the
  // guard above: this one covers the window where the run is known but its row is
  // not yet in `projects`.
  useEffect(() => {
    const runMode = threadRun?.agentMode;
    if (runMode && normalizeMode(runMode) !== agentMode) {
      setAgentModeState(normalizeMode(runMode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadRun?.agentMode]);

  // What approving a plan returns the session to. The REMEMBERING is local (a ref,
  // so it survives re-renders without causing one), the RULE is the shared
  // `planExitMode` — `ChatPanel` applies the same one, and a second copy would
  // diverge in exactly one direction: forgetting `manual` hands a user who asked to
  // approve every change an agent that no longer asks.
  //
  // The INITIAL value carries more weight than it used to. The composer now opens in
  // Plan, so the guard below does not fire at mount and this is what `planExitMode`
  // returns for a session that started planning and never left — i.e. it is what
  // makes *Approve & build — auto* the primary button on a brand-new project. Auto
  // is the right landing: approving a plan means "go do it".
  const preplanModeRef = useRef(MODE_AUTO);
  if (agentMode !== MODE_PLAN) preplanModeRef.current = agentMode;

  // `failure` is the banner's opening words, because the same write means two
  // different things to the user: changing the selector, and approving a plan (see
  // `handleApprovePlan`). One implementation, so the optimistic write and its revert
  // cannot be got right on one path and forgotten on the other.
  const handleSetAgentMode = useCallback(
    async (next, failure = "Couldn't change the assistant's mode") => {
      const mode = normalizeMode(next);
      if (!threadId) {
        // No project yet — hold it locally and let `startConversation` persist it.
        setAgentModeState(mode);
        return;
      }
      // Optimistic, then reverted on failure: the selector must never sit showing a
      // mode the backend is not enforcing.
      const previous = agentMode;
      setAgentModeState(mode);
      try {
        await setThreadMode(mode, undefined);
      } catch (err) {
        setAgentModeState(previous);
        setError(`${failure}: ${err.message || String(err)}`);
      }
    },
    [threadId, agentMode, setThreadMode]
  );

  // ── Approvals ──────────────────────────────────────────────────────────
  //
  // Two decisions a gated run can be waiting on, and until now BOTH were reachable
  // only by opening the project: `ProjectPage` wired them and this page wired
  // neither. Since the MODE is chosen here, a run gated from here that could only be
  // un-gated somewhere else was a dead end — pick Plan, send, read the plan on this
  // very screen, and then have nowhere to say yes.
  //
  // Approving a PLAN is a mode change, not an approval route: leaving plan mode IS
  // what starts the build, and `runner.set_mode` releases what the old mode was
  // holding. Hence `setThreadMode` rather than `approveThreadRun`.
  //
  // The MODE is now the user's explicit choice at that moment — "build it
  // unattended" or "ask me before each change" are two buttons, not one button and a
  // remembered ref. `planExitMode(preplanModeRef.current)` survives only as the
  // DEFAULT (which button is styled primary), because approving is still a mode
  // change and the mode has to come from somewhere if the caller names none.
  //
  // It goes through `handleSetAgentMode` for the OPTIMISTIC write, which is not a
  // polish detail here: the approve pair is rendered on `agentMode === MODE_PLAN`,
  // and this used to set no local state at all — it relied on adopting the run off
  // the response, which is `null` whenever nothing is live. So a successful approve
  // left both buttons sitting on screen, which is precisely how "nothing happens"
  // looked from the outside.
  const handleApprovePlan = useCallback(
    async (mode) =>
      handleSetAgentMode(mode || planExitMode(preplanModeRef.current), "Couldn't start the work"),
    [handleSetAgentMode]
  );

  // The REQUIREMENTS ROUND: every question in one submit. `skipped` is the Skip
  // control — the agent then proceeds on defaults and flags each one `assumed`.
  const handleAnswerQuestions = useCallback(
    async ({ answers, skipped }) => {
      // Deliberately NOT caught: `AgentQuestionCard` shows the failure beside its own
      // Submit and keeps the draft, which is where the user is looking. Swallowing it
      // into the page banner would clear nothing and explain less.
      await answerThreadQuestions(answers, undefined, { skipped });
    },
    [answerThreadQuestions]
  );

  // ONE PARKED MODULE. A subagent that called `request_user_input` blocks until
  // someone replies, and until now the only place to reply was the canvas tile —
  // `useAgentRun` has always returned `answer`, and this page simply never took
  // it. So a run watched entirely from this pane could stall on a question shown
  // right here with no way to answer it.
  //
  // Uncaught, like `handleAnswerQuestions`: `AgentParkedBanner` disables itself
  // while sending and sits where the user is looking, so a page-level banner
  // would explain less than the failure appearing in place.
  const handleAnswerParked = useCallback(
    async (tileId, value) => answerThreadParked(tileId, value, undefined),
    [answerThreadParked]
  );

  // Save the user's edited plan document. Also uncaught: the errors are line-numbered
  // and belong beside the textarea, not in a page-level banner.
  const handleSavePlan = useCallback(
    async (document) => saveThreadPlan(document, undefined),
    [saveThreadPlan]
  );

  // One held CALL. `decision` is `approve` | `approve_always` | `reject`; the
  // `approvalId` is server-minted and opaque, so a client cannot approve a call
  // other than the one it was shown.
  const handleApproveCall = useCallback(
    async (approvalId, decision) => {
      try {
        await approveThreadRun(approvalId, decision, undefined);
      } catch (err) {
        setError(`Couldn't record your decision: ${err.message || String(err)}`);
      }
    },
    [approveThreadRun]
  );

  useEffect(() => {
    reloadThread(threadId);
  }, [threadId, reloadThread]);

  const threadLive = !!threadRun && !['done', 'failed', 'cancelled'].includes(threadRun.status);

  // Poll the TRANSCRIPT on its own interval while the run is live.
  //
  // `useAgentRun` reloads it on a `rev` bump, which is not nearly often enough: `rev`
  // moves only when the record is written — at the turn's pop and at the post-turn
  // merge, so TWICE per turn — while the runner appends a message per ROUND, up to
  // sixteen of them. A turn that takes a minute therefore showed nothing at all until
  // it ended, which is what "the chat is not updating frequently enough" is.
  //
  // Same 2 s visibility-gated cadence as the run poll (`utils/polling.js`), so a
  // backgrounded tab costs nothing, and `reloadThread` no-ops when nothing changed.
  useEffect(() => {
    if (!threadId || !threadLive) return undefined;
    const id = pollingInterval(() => reloadThread(threadId), 2000);
    return () => clearInterval(id);
  }, [threadId, threadLive, reloadThread]);

  const threadProject =
    projects.find((p) => p.projectId === threadId) ||
    shared.find((p) => p.projectId === threadId) ||
    null;

  // A bound project that no longer exists releases the pane back to the composer.
  //
  // `threadId` is persisted, so without this a deleted project leaves the centre
  // pane bound to nothing: an empty transcript titled "Untitled project", with no
  // control that gets you back to the starting interface. It covers deleting the
  // open thread's project from the rail (which filters `projects`), and a stale id
  // in localStorage from a project deleted in another tab, on another device, or in
  // a previous session.
  //
  // **Gated on `!loading`, and that is the whole subtlety.** `projects` is `[]`
  // until `listProjects` resolves, so unbinding on "not found" alone would kill a
  // legitimately resumed thread on every single reload — the exact feature this is
  // meant to protect. `loading` is cleared in `fetchProjects`' `finally`, so by then
  // absence is real: `listProjects` returns every owned project (it hides only
  // system records, which are never bindable). The `shared` list needs no such wait
  // because a shared project is never bound in the first place — `/api/agent-run`
  // is owner-only.
  useEffect(() => {
    if (loading || !threadId) return;
    if (projects.some((p) => p.projectId === threadId)) return;
    bindThread(null);
  }, [loading, threadId, projects, bindThread]);

  // "Build it myself" — the composer's counterpart. `startConversation` creates a
  // project and starts a server agent run without navigating; this creates one and
  // navigates straight onto the canvas with nothing on it. Same module `/learn`
  // uses, so `ProjectPage`'s two handoff effects run their normal course.
  const startProject = useStartProject({
    // Dashboard owns the project list, so keep its optimistic insert (the same one
    // `handleComposerCreate` does below).
    onCreated: (project) => setProjects((prev) => [project, ...prev]),
    onError: setError,
  });

  const handleBuildManually = useCallback(async () => {
    // A LICENSE IS REQUIRED. This is the only control on an auth-only page that
    // navigates onto the license-gated `/project/:id`, so without one the click
    // would create a project and then bounce the user to /admin — an orphan project
    // and no canvas.
    //
    // UNREACHABLE as wired: the button is withheld by passing `onBuildManually`
    // undefined (see below), which is the rail's own idiom. It is here because that
    // prop is OPTIONAL, so "just always pass it" is the tempting simplification, and
    // it would silently delete the requirement. Keeping the check next to the thing
    // it protects turns that into a visible error instead.
    if (!hasValidLicense) {
      setError('An active license is required to open the workflow editor.');
      return;
    }
    // Release any bound conversation FIRST. The binding is persisted, so leaving it
    // in place would land the user back on the previous thread the next time they
    // return to the dashboard — the same reason `/projects`' "New project" calls
    // `clearBoundConversation()` before navigating. `bindThread(null)` rather than
    // that helper because this page owns the `threadId`/`threadMessages` state too.
    bindThread(null);
    await startProject({
      // The composer's answer for an empty prompt ('Untitled project'), reused
      // rather than retyped — there is nothing to derive a better name from, and
      // `summarizeProject` never fires on this path (it is guarded on a non-empty
      // description).
      name: deriveProjectName(''),
      // An empty context is what makes this a blank canvas rather than a kickoff:
      // `handleProblemStatementConfirm` saves no problem brief and queues no agent
      // turn, so the user lands on an idle editor.
      context: '',
      templateKey: null,
      // Written silently, and PERMANENTLY — there is no later UI to change a
      // project's unit system. This is the value the composer preselects, so the
      // two creation paths agree; a rail button has nowhere to put a picker.
      unitSystemId: DEFAULT_UNIT_SYSTEM,
    });
  }, [hasValidLicense, bindThread, startProject]);

  async function handleComposerCreate(payload) {
    try {
      const { project, run, error: runError, modeError } = await startConversation(payload);
      setProjects((prev) => [project, ...prev]);
      // Before `bindThread`, so the run is already in hand on the render that binds
      // it — the hook's own bootstrap read may not find a row written this instant.
      if (run) setStartedRun({ pid: project.projectId, run });
      bindThread(project.projectId);
      // The project exists either way; say so rather than leaving an invisible one.
      if (runError) setError(`Project created, but the assistant did not start: ${runError}`);
      else if (modeError)
        // The run IS gated as asked — only the sticky write failed, so the next run
        // in this project would revert to Auto. Worth saying, not worth alarming.
        setError(
          `The assistant is running in ${payload.agentMode} mode, but the setting ` +
            `didn't save for later runs: ${modeError}`
        );
      if (!runError)
        setThreadMessages([
          // `localMessage` defaults to the ASSISTANT role — this is the user's own
          // opening prompt, so it must say so or it renders as Dennis's.
          localMessage(buildProblemBrief(payload.context, payload.attachments), 'user'),
        ]);
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  async function handleThreadSend(text, attachments) {
    const message = buildProblemBrief(text, attachments);
    if (!message) return;
    setThreadSending(true);
    // Optimistic, and replaced on the next reload: `loop.run_turn` persists the
    // turn's prompt as a user message before the model runs, so the real one
    // arrives within a supervisor tick plus a poll interval.
    setThreadMessages((prev) => [...prev, localMessage(message, 'user')]);
    try {
      await startThreadRun(message, { groupId: DEFAULT_GROUP });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setThreadSending(false);
    }
  }

  async function handleTogglePin(project) {
    const next = !project.pinned;
    // Optimistic: starring is a sorting gesture, and a round-trip before the
    // row moves reads as a dropped click. Reverted below on failure.
    setProjects((prev) =>
      prev.map((p) => (p.projectId === project.projectId ? { ...p, pinned: next } : p))
    );
    try {
      await setProjectPinned(project.projectId, next);
    } catch (err) {
      setProjects((prev) =>
        prev.map((p) => (p.projectId === project.projectId ? { ...p, pinned: !next } : p))
      );
      setError(err.message);
    }
  }

  async function handleRename(id) {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      const updated = await renameProject(id, renameValue.trim());
      setProjects((prev) => prev.map((p) => (p.projectId === id ? updated : p)));
      setRenamingId(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.projectId !== id));
      setDeleteId(null);
    } catch (err) {
      setError(err.message);
    }
  }

  // The row's ⋯ menu closes itself (DashboardSidebar owns that state).
  async function handleCopy(project, owner) {
    try {
      const copy = await copyProject(project.projectId, { owner });
      setProjects((prev) => [copy, ...prev]);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleExport(project, owner) {
    try {
      const blob = await exportProject(project.projectId, owner);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name || 'project'}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleLeave(project) {
    try {
      await leaveSharedProject(project.projectId, project.ownerId);
      setShared((prev) => prev.filter((p) => p.projectId !== project.projectId));
      setLeaveTarget(null);
    } catch (err) {
      setError(err.message);
      setLeaveTarget(null);
    }
  }

  function startRename(project) {
    setRenamingId(project.projectId);
    setRenameValue(project.name);
  }

  function openProject(project) {
    if (project.shared) {
      navigate(`/project/${project.projectId}`, {
        state: { shared: true, owner: project.ownerId },
      });
    } else {
      navigate(`/project/${project.projectId}`);
    }
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <img
            className="dashboard-logo"
            src={`${process.env.PUBLIC_URL}/HIDENN_AI_logo.png`}
            alt="HIDENN AI"
          />
          <span className="dashboard-version" title={`Released ${versionInfo.released}`}>
            v{versionInfo.version}
          </span>
        </div>
        {/* `dashboard-header-nav` is what makes the six items below one width; it is a
            SEPARATE class because DatabasesPage reuses `dashboard-header-actions` for a
            one-child row that must not get those tracks. See Dashboard.css. */}
        <div className="dashboard-header-actions dashboard-header-nav">
          <button className="dash-nav-item" onClick={() => setRepoOpen(true)} title="Files">
            <span className="dash-nav-icon">
              <FolderIcon />
            </span>
            <span className="dash-nav-label">Files</span>
          </button>
          {/* Templates, guided tutorials and unattended server runs live here
              now — they used to be four setup controls on the composer. */}
          {/* These three are `<Link>`s, not buttons, so ctrl/⌘+click and
              middle-click open them in a new tab — the whole point of an anchor.
              Files above stays a button: it raises a modal and has no URL. */}
          <Link className="applink dash-nav-item" to="/learn" title="Learn">
            <span className="dash-nav-icon">
              <BookIcon />
            </span>
            <span className="dash-nav-label">Learn</span>
          </Link>
          <Link className="applink dash-nav-item" to="/databases" title="Databases">
            <span className="dash-nav-icon">
              <DatabaseIcon />
            </span>
            <span className="dash-nav-label">Databases</span>
          </Link>
          <Link className="applink dash-nav-item" to="/quality-check" title="Quality Check">
            <span className="dash-nav-icon">
              <CheckCircleIcon />
            </span>
            <span className="dash-nav-label">Quality Check</span>
          </Link>
          <ProcessMonitorButton variant="full" />
          <BugReportButton variant="full" />
          {/* Account dropdown: Profile / Team & Organization (AdminPage gates each
              section by role) / Settings / Sign out. Sign out stays Dashboard-only. */}
          <ProfileMenuButton onLogout={onLogout} />
        </div>
      </header>

      <div className="dashboard-body">
        <DashboardSidebar
          projects={projects}
          shared={shared}
          loading={loading}
          onSearchOpen={() => setSearchOpen(true)}
          // Releasing the binding IS "new project": the centre pane falls back to
          // the composer, and no project exists until something is typed into it.
          onNewProject={() => bindThread(null)}
          // Withheld without a license: unlike everything else on this page, this
          // one NAVIGATES onto the license-gated `/project/:id` route, so an
          // unlicensed click would create a project and immediately be bounced to
          // /admin — the orphan-project hazard `App.js` documents for `/learn`. The
          // rail already gates on the prop being present, so undefined is enough.
          onBuildManually={hasValidLicense ? handleBuildManually : undefined}
          sort={sort}
          onSortChange={setSort}
          renamingId={renamingId}
          renameValue={renameValue}
          onRenameValueChange={setRenameValue}
          onRenameSubmit={handleRename}
          onRenameCancel={() => setRenamingId(null)}
          onStartRename={startRename}
          onOpenProject={openProject}
          onSelectConversation={(project) => bindThread(project.projectId)}
          onTogglePin={handleTogglePin}
          onShare={setShareTarget}
          onCopy={handleCopy}
          onExport={handleExport}
          onAddQualityCheck={setQualityCheckTarget}
          onDelete={(project) => setDeleteId(project.projectId)}
          onLeave={setLeaveTarget}
        />

        {/* NOT `.dashboard-main` — DatabasesPage borrows that class from this
            stylesheet (it imports only its own CSS and relies on CRA bundling
            everything globally), so repurposing it would relayout that page. */}
        <main className="dashboard-centre">
          {error && (
            <div className="dashboard-error" onClick={() => setError(null)}>
              {error} <span>✕</span>
            </div>
          )}
          {threadId ? (
            <DashboardThread
              projectId={threadId}
              projectName={threadProject?.name}
              run={threadRun}
              messages={threadMessages}
              sending={threadSending}
              onSend={handleThreadSend}
              onCancel={() => cancelThreadRun(undefined)}
              // A 409 here is real (already held / not held), so it surfaces rather
              // than being swallowed — a Pause click that silently does nothing is
              // exactly the failure this control exists to fix.
              onPause={() => pauseThreadRun(undefined).catch((e) => setError(e.message))}
              onResume={() => resumeThreadRun(undefined).catch((e) => setError(e.message))}
              // A NEW TAB, not a navigation. Opening the module in place would tear
              // down the conversation the user is having in order to go do the one
              // thing the conversation asked them to do. `onOpenTile` stays a
              // CALLBACK rather than becoming a link: it is the documented host
              // policy that ChatMessageList's two hosts differ on — the editor
              // navigates in place, this one opens a tab.
              onOpenTile={(tileId) => openInNewTab(`/project/${threadId}/tile/${tileId}`)}
              agentMode={agentMode}
              onSetAgentMode={handleSetAgentMode}
              onApprovePlan={handleApprovePlan}
              onApproveCall={handleApproveCall}
              onAnswerQuestions={handleAnswerQuestions}
              onAnswerParked={handleAnswerParked}
              onSavePlan={handleSavePlan}
            />
          ) : (
            <DashboardComposer
              onCreate={handleComposerCreate}
              agentMode={agentMode}
              onSetAgentMode={handleSetAgentMode}
            />
          )}
        </main>
      </div>

      <FileRepository isOpen={repoOpen} onClose={() => setRepoOpen(false)} />

      {shareTarget && (
        <ShareProjectModal
          project={shareTarget}
          orgId={org?.orgId}
          onClose={() => setShareTarget(null)}
        />
      )}

      <AddQualityCheckModal
        open={!!qualityCheckTarget}
        project={qualityCheckTarget}
        onClose={() => setQualityCheckTarget(null)}
        onCreated={() => navigate('/quality-check')}
      />

      <SearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        projects={projects}
        shared={shared}
        onOpenProject={openProject}
      />

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete project?</h3>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDelete(deleteId)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {leaveTarget && (
        <div className="modal-overlay" onClick={() => setLeaveTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Remove yourself from this project?</h3>
            <p>
              "{leaveTarget.name}" will no longer appear in your shared projects. The owner can
              re-share it with you later.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setLeaveTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleLeave(leaveTarget)}>
                Remove me
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Row and overflow menus (and their backdrop) live in DashboardSidebar. */}
    </div>
  );
}
